<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { type ArticleCardResponse, getProjectArticles, type GetProjectDetailsProjectResponse } from '@/api';
import SectionHeader from '@/components/SectionHeader.vue';
import ArticleList from '@/components/articles/ArticleList.vue';
import { useSsgState } from '@/utilities/ssg-state.ts';
import { Constants } from '@/utilities/constants.ts';

const { project } = defineProps<{
  project: GetProjectDetailsProjectResponse;
}>();

const { t } = useI18n();

const articles = ref<ArticleCardResponse[] | null>(null);
const builderArticleCount = ref(0);

const loadArticles = async () => {
  const result = await getProjectArticles({ path: { projectId: project.id } });
  if (!result.data) {
    throw new Error(`Failed to fetch project articles. ${JSON.stringify(result.error)}`);
  }
  articles.value = result.data.items;
  builderArticleCount.value = result.data.builderArticleCount;
};

// Un article publié ou retiré dans la journée doit apparaître sans attendre le pré-rendu de nuit.
useSsgState({
  key: `project-articles:${project.id}`,
  refs: { articles, builderArticleCount },
  isLoaded: () => articles.value !== null,
  load: loadArticles,
  refreshOnClient: true,
});

watch(
  () => project.id,
  () => {
    void loadArticles();
  }
);

const hasMore = computed(() => builderArticleCount.value > (articles.value?.length ?? 0));
const builderRoute = computed(() => ({ name: 'builder', params: { slug: project.builder.name.toSlug(), builderId: project.builder.id }, hash: `#${Constants.ScrollAnchors.BuilderArticles}` }));
</script>

<template>
  <section v-if="articles && articles.length > 0">
    <SectionHeader>{{ t('views.project.articles.header', { name: project.name }) }}</SectionHeader>
    <ArticleList :articles="articles" />
    <p v-if="hasMore" class="mt-2 border-t border-gray-200 pt-3">
      <RouterLink :to="builderRoute" class="text-ph-green-700 hover:text-ph-green-600 text-sm font-semibold hover:underline">
        {{ t('views.project.articles.all', { builder: project.builder.name, count: builderArticleCount }) }}
      </RouterLink>
    </p>
  </section>
</template>
